"use client"

import { motion } from "framer-motion"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { Badge } from "@/shared/components/ui/badge"
import { Button } from "@/shared/components/ui/button"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion"
import { Logo } from "@/shared/components/logo"
import { cn } from "@/shared/lib/utils"

const AFFILIATE_SIGNUP = "https://affiliate.pleasur.ai/sign-up"
const AFFILIATE_LOGIN = "https://affiliate.pleasur.ai/login"

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] as const },
  }),
}

const steps = [
  { num: "1", key: "uniqueLink", iconName: "attach" as const },
  { num: "2", key: "share", iconName: "voice" as const },
  { num: "3", key: "earn", iconName: "bag-1" as const },
] as const

const stats = [
  { value: "40%", key: "revshare" },
  { value: "90", key: "cookie" },
  { value: "24h", key: "approval" },
  { value: "$100", key: "payout" },
] as const

const benefits = [
  { iconName: "bag" as const, key: "revshare" },
  { iconName: "flash" as const, key: "epc" },
  { iconName: "chart-square" as const, key: "dashboard" },
  { iconName: "shield-done" as const, key: "support" },
  { iconName: "bag-1" as const, key: "payouts" },
  { iconName: "bag-1" as const, key: "creatives" },
] as const

const partnerTypes = [
  { iconName: "video" as const, key: "creators" },
  { iconName: "upload" as const, key: "marketers" },
  { iconName: "monitor" as const, key: "agencies" },
] as const

const earningNotifications = [
  { amount: "$1,247.00", key: "earnedMonth" },
  { amount: "$89.00", key: "newCommission" },
  { amount: "$3,420.00", key: "paidOut" },
] as const

const materialKeys = ["banners", "emails", "landingPages", "socialPosts"] as const
const materialChecklistKeys = ["banners", "emails", "landingPages", "social"] as const
const earningChecklistKeys = ["recurring", "upgrades", "cookie", "noCaps"] as const
const commissionChecklistKeys = ["recurring", "noCaps", "upgrades", "cookie", "payouts"] as const
const faqKeys = [
  "earnings",
  "started",
  "commission",
  "clients",
  "cookie",
  "paid",
  "adultSites",
  "restrictions",
  "approval",
] as const

export function AffiliateContent() {
  const t = useTranslations("affiliate")

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-transparent" />

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 flex items-center justify-center gap-3"
          >
            <Logo size="md" />
            <div className="h-5 w-px bg-border/40" />
            <span className="text-sm font-semibold uppercase text-muted-foreground">
              {t("hero.program")}
            </span>
          </motion.div>

          <motion.h1
            className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t.rich("hero.title", {
              highlight: (chunks) => <span className="text-emerald-400">{chunks}</span>,
            })}
          </motion.h1>

          <motion.p
            className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            {t.rich("hero.description", {
              strong: (chunks) => <span className="font-semibold text-foreground">{chunks}</span>,
            })}
          </motion.p>

          <motion.div
            className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Button
              asChild
              size="lg"
              className={cn(
                "min-h-[48px] px-8 text-sm font-bold",
                "bg-white text-black hover:bg-white/90",
              )}
            >
              <a href={AFFILIATE_SIGNUP}>
                {t("hero.apply")}
                <Icon name="arrow-right" size={16} className="ml-2 size-4" />
              </a>
            </Button>
          </motion.div>

          <motion.p
            className="mt-4 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {t("hero.alreadyPartner")}{" "}
            <a href={AFFILIATE_LOGIN} className="font-medium text-foreground hover:underline">
              {t("hero.login")}
            </a>
          </motion.p>
        </div>
      </section>

      <section className="border-y border-border/20 bg-card/30 py-10 sm:py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {stats.map((item, i) => (
              <motion.div
                key={item.key}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                className="text-center"
              >
                <span className="block text-2xl font-extrabold text-foreground sm:text-3xl">{item.value}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{t(`stats.${item.key}`)}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t("howItWorks.eyebrow")} title={t("howItWorks.title")} />

          <div className="mt-16 grid gap-8 sm:grid-cols-3">
            {steps.map((step, i) => (
              <motion.div
                key={step.key}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="group relative text-center"
              >
                <div className="mx-auto mb-6 flex size-14 items-center justify-center rounded-2xl border border-border/50 bg-card text-xl font-extrabold text-foreground">
                  {step.num}
                </div>
                {i < steps.length - 1 && (
                  <div className="absolute left-[calc(50%+36px)] top-7 hidden h-px w-[calc(100%-72px)] bg-border/30 sm:block" />
                )}
                <h3 className="text-lg font-bold text-foreground">{t(`steps.${step.key}.title`)}</h3>
                <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">
                  {t(`steps.${step.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4 border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
                {t("earnings.eyebrow")}
              </Badge>
              <h2 className="text-3xl font-bold leading-tight text-foreground sm:text-4xl">
                {t.rich("earnings.title", {
                  highlight: (chunks) => <span className="text-emerald-400">{chunks}</span>,
                })}
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                {t.rich("earnings.description", {
                  strong: (chunks) => <span className="font-medium text-foreground">{chunks}</span>,
                })}
              </p>
              <Checklist items={earningChecklistKeys.map((key) => t(`earnings.checklist.${key}`))} />
              <Button
                asChild
                size="lg"
                className="mt-8 min-h-[48px] bg-emerald-500 px-8 font-bold text-white hover:bg-emerald-400"
              >
                <a href={AFFILIATE_SIGNUP}>
                  {t("earnings.cta")}
                  <Icon name="arrow-right" size={16} className="ml-2 size-4" />
                </a>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative"
            >
              <div className="surface-subtle rounded-2xl p-6">
                <div className="mb-4 flex items-center gap-2">
                  <div className="size-3 rounded-full bg-emerald-400" />
                  <span className="text-sm font-medium text-foreground">{t("dashboard.title")}</span>
                </div>
                <div className="mb-6 rounded-xl bg-muted/50 p-4">
                  <div className="mb-3 flex items-end justify-between">
                    <span className="text-xs text-muted-foreground">{t("dashboard.thisMonth")}</span>
                    <span className="text-xl font-bold text-emerald-400">$4,827.00</span>
                  </div>
                  <div className="flex h-20 items-end gap-1.5">
                    {[35, 42, 28, 55, 48, 62, 70, 58, 75, 82, 68, 90].map((height, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-emerald-500 to-emerald-400"
                        style={{ height: `${height}%` }}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  {earningNotifications.map((notification, i) => (
                    <motion.div
                      key={notification.key}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                      className="flex items-center justify-between rounded-lg border border-border/30 bg-background/50 px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex size-8 items-center justify-center rounded-full bg-emerald-400/10">
                          <Icon name="bag-1" size={16} className="size-4 text-emerald-400" />
                        </div>
                        <div>
                          <span className="block text-sm font-semibold text-foreground">{notification.amount}</span>
                          <span className="text-xs text-muted-foreground">{t(`dashboard.notifications.${notification.key}.label`)}</span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{t(`dashboard.notifications.${notification.key}.time`)}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, type: "spring" }}
                className="absolute -right-4 -top-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 shadow-lg backdrop-blur-sm"
              >
                <span className="flex items-center gap-1.5 text-sm font-bold text-emerald-400">
                  <Icon name="graph" size={16} className="size-4" />
                  {t("dashboard.growth")}
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-t border-border/30 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                {materialKeys.map((key, i) => (
                  <motion.div
                    key={key}
                    custom={i}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="rounded-xl border border-border/40 bg-card/50 p-5"
                  >
                    <span className="block text-2xl font-extrabold text-foreground">{t(`materials.cards.${key}.count`)}</span>
                    <span className="text-sm text-muted-foreground">{t(`materials.cards.${key}.label`)}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <Badge className="mb-4 border border-border/40 bg-foreground/5 px-3 py-1 text-xs font-semibold text-muted-foreground">
                {t("materials.eyebrow")}
              </Badge>
              <h2 className="text-3xl font-bold leading-tight text-foreground sm:text-4xl">
                {t("materials.title")}
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                {t("materials.description")}
              </p>
              <Checklist items={materialChecklistKeys.map((key) => t(`materials.checklist.${key}`))} iconClassName="text-purple-400" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-t border-border/30 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={t("benefits.eyebrow")}
            title={t("benefits.title")}
            description={t("benefits.description")}
          />

          <motion.div
            className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {benefits.map((item, i) => (
              <motion.div
                key={item.key}
                custom={i}
                variants={fadeUp}
                className="group rounded-2xl border border-border/40 bg-card/40 p-6 transition-all hover:border-border/60 hover:bg-card/80"
              >
                <div className="flex size-12 items-center justify-center rounded-xl bg-white/5 transition-colors group-hover:bg-white/10">
                  <Icon name={item.iconName} size={24} className="size-6 text-muted-foreground transition-colors group-hover:text-foreground" />
                </div>
                <h3 className="mt-4 font-bold text-foreground">{t(`benefits.items.${item.key}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`benefits.items.${item.key}.description`)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-border/30 py-20 sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.01] to-transparent" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t("partners.title")} description={t("partners.description")} />

          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {partnerTypes.map((item, i) => (
              <motion.div
                key={item.key}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="group rounded-2xl border border-border/40 bg-card/40 p-8 text-center transition-all hover:border-border/60 hover:bg-card/60"
              >
                <div className="mx-auto flex size-14 items-center justify-center rounded-2xl border border-border/30 bg-card transition-colors">
                  <Icon name={item.iconName} size={28} className="size-7 text-muted-foreground transition-colors group-hover:text-foreground" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-foreground">{t(`partners.items.${item.key}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`partners.items.${item.key}.description`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border/30 py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl border border-border/40 bg-card p-8 sm:p-14"
          >
            <Badge className="mb-6 border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
              {t("commission.eyebrow")}
            </Badge>

            <div className="flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-6xl font-extrabold text-emerald-400 sm:text-7xl">40</span>
                  <span className="text-4xl font-extrabold text-emerald-400 sm:text-5xl">%</span>
                </div>
                <p className="mt-2 text-lg font-bold text-foreground">{t("commission.title")}</p>
                <p className="text-muted-foreground">{t("commission.subtitle")}</p>
              </div>

              <ul className="space-y-3 text-sm">
                {commissionChecklistKeys.map((key) => (
                  <li key={key} className="flex items-center gap-2.5 text-muted-foreground">
                    <Icon name="tick" size={16} className="size-4 shrink-0 text-emerald-400" />
                    <span>{t(`commission.checklist.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-border/30 py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t("faq.title")} />

          <motion.div
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Accordion type="single" collapsible className="w-full space-y-2">
              {faqKeys.map((key) => (
                <AccordionItem
                  key={key}
                  value={key}
                  className="rounded-xl border border-border/40 bg-card/30 px-5 data-[state=open]:bg-card/60"
                >
                  <AccordionTrigger className="py-5 text-left font-semibold text-foreground hover:no-underline">
                    {t(`faq.items.${key}.question`)}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 leading-relaxed text-muted-foreground">
                    {t(`faq.items.${key}.answer`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-border/30 py-20 sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-t from-white/[0.02] via-transparent to-transparent" />

        <div className="relative z-10 mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              {t("bottomCta.title")}
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-muted-foreground">
              {t.rich("bottomCta.description", {
                highlight: (chunks) => <span className="font-medium text-emerald-400">{chunks}</span>,
              })}
            </p>
            <Button
              asChild
              size="lg"
              className="mt-8 min-h-[48px] bg-white px-8 text-sm font-bold text-black hover:bg-white/90"
            >
              <a href={AFFILIATE_SIGNUP}>
                {t("bottomCta.button")}
                <Icon name="arrow-right" size={16} className="ml-2 size-4" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string
  title: string
  description?: string
}) {
  return (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      {eyebrow ? (
        <Badge className="mb-4 border border-border/40 bg-white/5 px-3 py-1 text-xs font-semibold text-muted-foreground">
          {eyebrow}
        </Badge>
      ) : null}
      <h2 className="text-3xl font-bold text-foreground sm:text-4xl">{title}</h2>
      {description ? (
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">{description}</p>
      ) : null}
    </motion.div>
  )
}

function Checklist({
  items,
  iconClassName = "text-emerald-400",
}: {
  items: string[]
  iconClassName?: string
}) {
  return (
    <ul className="mt-6 space-y-3">
      {items.map((text) => (
        <li key={text} className="flex items-start gap-3 text-sm text-muted-foreground">
          <Icon name="tick" size={16} className={cn("mt-0.5 size-4 shrink-0", iconClassName)} />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  )
}
